import styled from "@emotion/styled";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button as AIMEButton } from "tot-loft-component-library";
import { PaartnerLogo } from "./assets";
import GenericModal from "./GenericModal";
import ModalSuccess from "./ModalSuccess";
import * as PRTNR from "./prtnr";
import { createOrUpdateStripeAccount } from "./redux/slices/MembersThunks";


const validateNationalInsuranceNumber = (niNumber) => {
  if (!niNumber || typeof niNumber !== "string") {
    return "National Insurance Number is required.";
  }

  const cleanNI = niNumber.replace(/\s/g, "").toUpperCase();

  if (cleanNI.length !== 9) {
    return "National Insurance Number must be exactly 9 characters long.";
  }

  if (!/^[A-Z]{2}\d{6}[A-Z]$/.test(cleanNI)) {
    return "Must follow format: 2 letters, 6 numbers, 1 letter (e.g., AB123456C).";
  }

  const firstChar = cleanNI[0];
  const secondChar = cleanNI[1];
  const lastChar = cleanNI[8];
  const firstTwoChars = cleanNI.substring(0, 2);

  const invalidChars = ["D", "F", "I", "Q", "U", "V"];
  if (invalidChars.includes(firstChar)) {
    return "First character cannot be D, F, I, Q, U, or V.";
  }
  if (invalidChars.includes(secondChar)) {
    return "Second character cannot be D, F, I, Q, U, or V.";
  }
  if (secondChar === "O") {
    return "Second character cannot be O.";
  }

  const invalidCombinations = ["BG", "GB", "KN", "NK", "NT", "TN", "ZZ"];
  if (invalidCombinations.includes(firstTwoChars)) {
    return `First two characters cannot be ${firstTwoChars}.`;
  }

  if (!["A", "B", "C", "D"].includes(lastChar)) {
    return "Final character must be A, B, C, or D.";
  }

  return ""; 
};

export default function AccountSetup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    accountHolderName: "",
    accountNumber: "",
    confirmAccountNumber: "",
    ifscSwiftCode: "",
    dateOfBirth: "",
    streetAddress: "",
    postalCode: "",
    nationalInsuranceNumber: "",
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [openSuccessModal, setopenSuccessModal] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setHasSubmitted(true);
    setGeneralError(null);
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      setGeneralError("Account numbers do not match.");
      return;
    }
    const requiredFields = [
      "accountHolderName",
      "accountNumber",
      "confirmAccountNumber",
      "ifscSwiftCode",
      "dateOfBirth",
      "streetAddress",
      "postalCode",
    ];
    const missing = requiredFields.filter((field) => !formData[field]?.trim());
    if (missing.length > 0) {
      setGeneralError("Please fill in all required fields.");
      return;
    }

    // --- Account number format ---
    // if (
    //   formData.accountNumber.length < 6 ||
    //   formData.accountNumber.length > 18
    // ) {
    //   setGeneralError("Account number must be 6–18 characters long.");
    //   return;
    // }

    // --- IFSC/SWIFT format ---
    if (!/^[A-Z0-9]{4,11}$/i.test(formData.ifscSwiftCode)) {
      setGeneralError("Enter a valid Sort Code.");
      return;
    }

    const dob = new Date(formData.dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const ageCheck =
      age > 18 ||
      (age === 18 &&
        (today.getMonth() > dob.getMonth() ||
          (today.getMonth() === dob.getMonth() &&
            today.getDate() >= dob.getDate())));
    if (!ageCheck) {
      setGeneralError(
        "You must be at least 18 years old to create a Stripe account."
      );
      return;
    }

    if (!/^[A-Za-z0-9\- ]{3,10}$/.test(formData.postalCode)) {
      setGeneralError("Enter a valid postal code.");
      return;
    }
    const niError = validateNationalInsuranceNumber(
      formData.nationalInsuranceNumber
    );
    if (niError) {
      setGeneralError(niError);
      return;
    }

    const [year, month, day] = formData.dateOfBirth.split("-");

    const payload = {
      account_holder_name: formData.accountHolderName,
      account_number: formData.accountNumber,
      confirm_account_number: formData.confirmAccountNumber,
      sort_code: formData.ifscSwiftCode || formData.sortCode,
      date_of_birth: {
        day: parseInt(day, 10),
        month: parseInt(month, 10),
        year: parseInt(year, 10),
      },
      home_address: formData.streetAddress,
      postal_code: formData.postalCode,
      nino: formData.nationalInsuranceNumber,
    };

    setIsSubmitting(true);
    try {
      const res = await dispatch(createOrUpdateStripeAccount(payload)).unwrap();

      if (res?.error) {
        setGeneralError(res.payload || "Login failed");
        return;
      }
      setIsSubmitting(false);
      setopenSuccessModal(true);
    } catch (e) {
      setGeneralError(e || "Something went wrong. Please try again.");
      setopenSuccessModal(false);
      setIsSubmitting(false);
    }
  };
  return (
    <RootWrapperSignupReferrerIndiviual>
      <Header>
        <HeaderWrapper>
          <Left>
            <Logo
              onClick={() => navigate("/")}
              src={PaartnerLogo}
              alt="Paartner Logo"
            />
          </Left>
     
        </HeaderWrapper>
      </Header>
      <Section>
        <Wrapper>
          <SignupSignin>
            <ContentBlock1>
              <Content>
                <EarningCashForMakingReferralsIsEasierThanEver>
                  Account Stripe Setup
                </EarningCashForMakingReferralsIsEasierThanEver>
                <DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed>
                  To start using Stripe for payment processing, set up your
                  account.
                </DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed>
              </Content>
            </ContentBlock1>

            <Frame58>
              <Frame1409>
                <PRTNR.Input
                  label="Account Holder Name"
                  value={formData.accountHolderName}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      accountHolderName: e.target.value,
                    });
                  }}
                  required
                  error={hasSubmitted && !formData.accountHolderName}
                  errorMessage={"Required Field"}
                />
              </Frame1409>

              <PRTNR.Input
                label="Account Number"
                value={formData.accountNumber}
                onChange={(e) => {
                  setFormData({ ...formData, accountNumber: e.target.value });
                }}
                required
                error={hasSubmitted && !formData.accountNumber}
                errorMessage={"Required Field"}
              />
              <PRTNR.Input
                label="Confirm Account Number"
                value={formData.confirmAccountNumber}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    confirmAccountNumber: e.target.value,
                  });
                }}
                required
                error={hasSubmitted && !formData.confirmAccountNumber}
                errorMessage={"Required Field"}
              />

              <Frame1409>
                <PRTNR.Input
                  label="Sort Code"
                  value={formData.ifscSwiftCode}
                  onChange={(e) => {
                    setFormData({ ...formData, ifscSwiftCode: e.target.value });
                  }}
                  required
                  error={hasSubmitted && !formData.ifscSwiftCode}
                  errorMessage={"Required Field"}
                />

                <PRTNR.Input
                  label="Date of birth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => {
                    setFormData({ ...formData, dateOfBirth: e.target.value });
                  }}
                  required
                  error={hasSubmitted && !formData.dateOfBirth}
                  errorMessage={"Required Field"}
                />
              </Frame1409>

              <Frame1409>
                <PRTNR.Input
                  label="Street address"
                  value={formData.streetAddress}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      streetAddress: e.target.value,
                    });
                  }}
                  required
                  error={hasSubmitted && !formData.streetAddress}
                  errorMessage={"Required Field"}
                />
                <PRTNR.Input
                  label="Postal code"
                  value={formData.postalCode}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      postalCode: e.target.value,
                    });
                  }}
                  required
                  error={hasSubmitted && !formData.postalCode}
                  errorMessage={"Required Field"}
                />
              </Frame1409>
              <PRTNR.Input
                label="National Insurance Number"
                value={formData.nationalInsuranceNumber}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    nationalInsuranceNumber: e.target.value,
                  });
                }}
                required
                error={hasSubmitted && !formData.nationalInsuranceNumber}
                errorMessage={"Required Field"}
              />

              {generalError && <ErrorMessage>{generalError}</ErrorMessage>}
              <AIMEButton
                size="small"
                onClick={() => {
                  handleSubmit();
                }}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "row",
                  alignItems: "center",
                  flex: "none",
                  gap: "10px",
                  borderRadius: "48px",
                  width: "320px",
                  background: "rgb(33, 37, 67)",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  boxSizing: "border-box",
                  padding: "16px 32px",
                  "&:hover": {
                    background: "rgb(33, 37, 67)",
                    opacity: "0.9",
                  },
                  "@media (max-width: 768px)": {
                    width: "260px",
                  },
                  "@media (max-width: 480px)": {
                    width: "100%",
                  },
                }}
              >
                <span
                  style={{
                    color: "white",
                    textOverflow: "ellipsis",
                    fontSize: "16px",
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: "600",
                    lineHeight: "110%",
                    textAlign: "left",
                  }}
                >
                {isSubmitting ? "Submitting..." : "Submit"}  
                </span>
              </AIMEButton>
              <PRTNR.Button
                variant="gray-light"
                onClick={() => {
                  window.location.href = process.env.REACT_APP_AUTH_BASE_URL;
                }}
              >
                Skip
              </PRTNR.Button>
            </Frame58>
          </SignupSignin>
        </Wrapper>
      </Section>

      <GenericModal
        isOpen={openSuccessModal}
        onClose={() => {
          setopenSuccessModal(false);
        }}
      >
        <ModalSuccess
          isOpen={openSuccessModal}
          onClose={() => {
            setopenSuccessModal(false);
            window.location.href = process.env.REACT_APP_AUTH_BASE_URL;
          }}
        />
      </GenericModal>
    </RootWrapperSignupReferrerIndiviual>
  );
}

const RootWrapperSignupReferrerIndiviual = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  flex: 1;
  border-radius: 12px;
  box-sizing: border-box;
  padding: 24px 16px;
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 165px;
  height: 24px;
  position: relative;
`;

const Section = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const SignupSignin = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const ContentBlock1 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 960px;
  margin: 0 auto;
  box-sizing: border-box;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
    padding-inline: 24px;
  }
`;

const EarningCashForMakingReferralsIsEasierThanEver = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(24px, 2.5vw, 48px);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: -1px;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(16px, 2vw, 20px);
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame58 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  border: solid 1px rgba(136, 136, 136, 0.1);
  border-radius: 32px;
  align-self: stretch;
  background: rgb(226, 232, 240);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 32px;
  flex-shrink: 0;
  max-width: 560px;
  margin: 0 auto;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const Frame1409 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: left;
  width: 100%;
`;
